/**
 * Canonical dealer profile contract constants (Dealer Profile M2).
 */

export const DEALER_PROFILE_VERIFICATION_STATUS = {
  VERIFIED: "verified",
  PENDING: "pending",
  UNVERIFIED: "unverified",
  REJECTED: "rejected",
  SUSPENDED: "suspended",
  UNKNOWN: "unknown",
};

export const DEALER_PROFILE_PUBLIC_STATUS = {
  PUBLIC: "public",
  HIDDEN: "hidden",
  INACTIVE: "inactive",
  SUSPENDED: "suspended",
  REJECTED: "rejected",
  DELETED: "deleted",
  UNKNOWN: "unknown",
};

export const DEALER_PROFILE_VERIFICATION_LABELS_ES = {
  verified: "Concesionario verificado",
  pending: "Verificación pendiente",
  unverified: "Concesionario no verificado",
  rejected: "Concesionario no disponible",
  suspended: "Concesionario suspendido",
  unknown: "Verificación no disponible",
};

export const DEALER_PROFILE_VERIFICATION_DESCRIPTIONS_ES = {
  verified: "AutoMapa revisó información básica del concesionario.",
  pending: "La verificación de este concesionario está pendiente.",
  unverified: "AutoMapa aún no ha verificado este concesionario.",
  rejected: "Este concesionario no está disponible en AutoMapa.",
  suspended: "Este concesionario no está disponible en AutoMapa.",
  unknown: "No tenemos información de verificación disponible.",
};

export const DEALER_PROFILE_CONTACT_DISABLED_REASONS = {
  DEALER_MISSING: "dealer_missing",
  DEALER_HIDDEN: "dealer_hidden",
  DEALER_INACTIVE: "dealer_inactive",
  DEALER_SUSPENDED: "dealer_suspended",
  DEALER_REJECTED: "dealer_rejected",
  DEALER_DELETED: "dealer_deleted",
  CONTACT_MISSING: "contact_missing",
  CONTACT_INVALID: "contact_invalid",
  LISTING_UNAVAILABLE: "listing_unavailable",
  LISTING_NOT_PUBLIC: "listing_not_public",
  LISTING_DEALER_MISMATCH: "listing_dealer_mismatch",
  LEADS_DISABLED: "leads_disabled",
  UNKNOWN: "unknown",
};

export const DEALER_PROFILE_CONTACT_DISABLED_LABELS_ES = {
  dealer_missing: "Concesionario por confirmar.",
  dealer_hidden: "Este concesionario no está disponible en este momento.",
  dealer_inactive: "Este concesionario no está disponible en este momento.",
  dealer_suspended: "Este concesionario no está disponible en este momento.",
  dealer_rejected: "Este concesionario no está disponible en AutoMapa.",
  dealer_deleted: "Este concesionario no está disponible en AutoMapa.",
  contact_missing: "Contacto no disponible.",
  contact_invalid: "Contacto no disponible.",
  listing_unavailable: "Este vehículo ya no está disponible.",
  listing_not_public: "Contacto no disponible.",
  listing_dealer_mismatch: "Contacto no disponible.",
  leads_disabled: "Contacto no disponible por ahora.",
  unknown: "Contacto no disponible.",
  // Legacy aliases (M11)
  dealer_contact_missing: "Contacto por confirmar.",
  no_whatsapp: "Contacto no disponible.",
  dealer_unavailable: "Este concesionario no está disponible en este momento.",
};

export const DEALER_PROFILE_FALLBACK_COPY = {
  name: "Concesionario por confirmar",
  displayName: "Concesionario por confirmar",
  city: "Ubicación por confirmar",
  location: "Ubicación por confirmar",
  verification: "Verificación no disponible",
  inventoryCount: "Inventario por confirmar",
  contact: "Contacto no disponible",
  responseTime: null,
};

export function isDealerProfileLocationFallbackLabel(label) {
  if (!label) return true;
  const normalized = String(label).trim().toLowerCase();
  return (
    normalized === DEALER_PROFILE_FALLBACK_COPY.location.toLowerCase() ||
    normalized === DEALER_PROFILE_FALLBACK_COPY.city.toLowerCase() ||
    normalized === "location pending confirmation"
  );
}

export const DEALER_PROFILE_CONTACT_COPY_ES = {
  whatsapp: "Contactar por WhatsApp",
  contactDealer: "Contactar concesionario",
  requestInfo: "Solicitar información",
  phone: "Llamar",
  viewProfile: "Ver perfil del concesionario",
  unavailable: "Contacto no disponible",
  unavailableNow: "Contacto no disponible por ahora.",
};

export const DEALER_PROFILE_TRUST_NOTE_ES =
  "Antes de visitar o separar un vehículo, confirma disponibilidad, precio, documentos y condiciones directamente con el concesionario.";

export const DEALER_PROFILE_HERO_TRUST_NOTE_ES = {
  verified:
    "AutoMapa revisó información básica del concesionario. Antes de comprar, confirma disponibilidad, precio, documentos y estado del vehículo.",
  default:
    "Antes de visitar o separar un vehículo, confirma disponibilidad, precio, documentos y datos del concesionario.",
};

export const DEALER_PROFILE_INVENTORY_LABELS_ES = {
  unknown: "Inventario por confirmar",
  empty: "Sin vehículos disponibles por ahora",
  one: "1 vehículo disponible",
  many: (count) => `${count} vehículos disponibles`,
};

/** Buyer-safe trust panel copy (Dealer Profile M6). */
export const DEALER_PROFILE_TRUST_PANEL_SECTIONS_ES = {
  dealerStatus: "Estado del concesionario",
  whatReviewed: "Lo que AutoMapa revisó",
  beforeVisit: "Antes de visitar o separar un vehículo",
  contactInventory: "Contacto e inventario",
};

export const DEALER_PROFILE_TRUST_PANEL_VERIFICATION_EXPLANATIONS_ES = {
  verified:
    "AutoMapa revisó información básica del concesionario. Esto no reemplaza una revisión del vehículo, documentos ni condiciones de compra.",
  pending:
    "La verificación de este concesionario está pendiente. Confirma datos, dirección, disponibilidad y documentos antes de avanzar.",
  unverified:
    "AutoMapa aún no ha verificado este concesionario. Antes de separar un vehículo, confirma datos del vendedor, documentos y condiciones.",
  unknown: "No tenemos información suficiente sobre la verificación de este concesionario.",
};

export const DEALER_PROFILE_TRUST_PANEL_WHAT_REVIEWED_ES = {
  verified: "Datos básicos del negocio, ubicación y canal de contacto.",
  pending: "La revisión aún está en proceso.",
  unverified: "AutoMapa aún no ha completado una revisión básica de este concesionario.",
  unknown: "No hay detalle de revisión disponible.",
};

export const DEALER_PROFILE_TRUST_SIGNAL_LABELS_ES = {
  verification: "Verificación",
  inventory: "Inventario",
  contact: "Contacto",
  location: "Ubicación",
  activity: "Actividad",
};

export const DEALER_PROFILE_TRUST_SIGNAL_FALLBACKS_ES = {
  inventory: "Inventario por confirmar",
  contact: "Contacto no disponible",
  location: "Ubicación por confirmar",
  activity: "Actividad por confirmar",
  contactWhatsapp: "WhatsApp disponible",
  contactPhone: "Teléfono disponible",
};

export const DEALER_PROFILE_TRUST_PANEL_INVENTORY_MESSAGES_ES = {
  unknown: "El inventario público está por confirmar.",
  empty: "Este concesionario no tiene vehículos disponibles por ahora.",
  one: "Este concesionario tiene 1 vehículo disponible en AutoMapa.",
  many: (count) => `Este concesionario tiene ${count} vehículos disponibles en AutoMapa.`,
};

export const DEALER_PROFILE_TRUST_PANEL_CONTACT_MESSAGES_ES = {
  available:
    "Antes de visitar o separar un vehículo, confirma disponibilidad, precio, documentos y condiciones directamente con el concesionario.",
  unavailable:
    "El contacto de este concesionario no está disponible por ahora. Puedes seguir buscando vehículos disponibles en AutoMapa.",
};

export const DEALER_PROFILE_TRUST_PANEL_CHECKLIST_ITEMS_ES = [
  "Confirmar que el vehículo sigue disponible",
  "Confirmar que el precio publicado sigue vigente",
  "Revisar SOAT y tecnomecánica",
  "Pedir historial o documentos del vehículo",
  "Considerar un peritaje independiente",
  "Confirmar condiciones de separación y traspaso",
];

export const DEALER_PROFILE_TRUST_PANEL_CHECKLIST_CTA_ES = "Ver checklist de compra";

/** Dealer inventory grid copy (Dealer Profile M7). */
export const DEALER_PROFILE_INVENTORY_GRID_COPY_ES = {
  heading: "Vehículos disponibles",
  subheading: (dealerName) =>
    `Inventario público de ${dealerName || "este concesionario"} en AutoMapa.`,
  countMany: (count) => `${count} vehículos disponibles`,
  countOne: "1 vehículo disponible",
  countFallback: "Inventario disponible",
  loading: "Cargando vehículos…",
  errorTitle: "No pudimos cargar los vehículos de este concesionario.",
  errorBody: "Puedes intentar nuevamente o seguir buscando en AutoMapa.",
  retry: "Reintentar",
  emptyTitle: "Este concesionario no tiene vehículos disponibles por ahora.",
  emptyBody: "Puedes seguir buscando opciones similares en AutoMapa.",
  searchCity: (city) => `Buscar en ${city}`,
  loadMore: "Ver más vehículos",
};

/** Dealer inventory card copy (Dealer Profile M10). */
export const DEALER_INVENTORY_CARD_COPY_ES = {
  dealerContext: "Publicado por este concesionario",
  viewDetail: "Ver detalle",
  contact: "WhatsApp",
  contactAlt: "Contactar",
  contactUnavailable: "Contacto no disponible",
  availabilityConfirm: "Disponibilidad por confirmar",
};

export const DEALER_PROFILE_ANALYTICS_EVENTS = {
  INVENTORY_CARD_CLICKED: "dealer_inventory_card_clicked",
};

export {
  DEALER_PROFILE_ANALYTICS_EVENTS as DEALER_PROFILE_PAGE_ANALYTICS_EVENTS,
  DEALER_PROFILE_ANALYTICS_SOURCE_COMPONENTS,
} from "./dealerProfileAnalyticsConstants";

/** Public dealer profile route prefix under client view runtime. */
export const PUBLIC_DEALER_PROFILE_PATH_PREFIX = "/v/automap/concesionarios";

/** Canonical dealer profile page public states (M4). */
export const DEALER_PROFILE_PAGE_STATE = {
  PUBLIC: "public",
  PUBLIC_NO_INVENTORY: "public_no_inventory",
  PUBLIC_CONTACT_LIMITED: "public_contact_limited",
  HIDDEN: "hidden",
  INACTIVE: "inactive",
  SUSPENDED: "suspended",
  REJECTED: "rejected",
  DELETED: "deleted",
  INVALID: "invalid",
  NOT_FOUND: "not_found",
  UNKNOWN: "unknown",
  ERROR: "error",
};

export const DEALER_PUBLIC_STATE_MESSAGES_ES = {
  hidden: "Este concesionario no está disponible públicamente.",
  inactive: "Este concesionario no está disponible en este momento.",
  suspended: "Este concesionario no está disponible en AutoMapa.",
  rejected: "Este concesionario no está disponible en AutoMapa.",
  deleted: "Este concesionario no está disponible en AutoMapa.",
  unknown: "Este concesionario no está disponible en AutoMapa.",
  not_found: "No encontramos este concesionario.",
  invalid: "No encontramos este concesionario.",
  error: "No pudimos cargar este concesionario.",
  public_no_inventory: "Este concesionario no tiene vehículos disponibles por ahora.",
  public_no_inventory_contact_note:
    "Puedes consultar si el concesionario tiene vehículos disponibles fuera de AutoMapa.",
  public_contact_limited: "El contacto de este concesionario no está disponible por ahora.",
  unavailable_body: "Puedes seguir buscando vehículos disponibles en AutoMapa.",
  not_found_body: "Puede que el perfil haya cambiado o ya no esté disponible.",
  error_body: "Intenta nuevamente o vuelve a la búsqueda.",
  search_cta: "Ver carros usados",
  directory_cta: "Ver concesionarios disponibles",
};
